import { useState } from "react";
import { Button, FormField, Modal, TextInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import type { Invoice } from "@/lib/api/types";
import {
  undeliverableMessage,
  undeliverableReason,
} from "@/lib/invoice/place";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";

/**
 * CORRECTING WHO AN OFF-PREMISE BILL IS FOR (B9, B107).
 *
 * `OffPremiseDialog` asks these three questions once, at open, and until this
 * existed nothing asked them again: a mis-typed digit in a rider's phone number
 * had no remedy but cancelling a bill that had already burnt a daily sequence,
 * and only while it was still empty. The person who took the order down the
 * phone is the one who hears "no, it's flat 3, not 5".
 *
 * THE SAME THREE FIELDS AS THE OPEN DIALOG, in the same order, with the same
 * bounds and the same rules — the address on DELIVERY only, the phone forced
 * LTR because a number typed into an RTL box reorders its own digits and this
 * one gets dialled. A correction screen that looked different from the screen
 * being corrected would be a second place for the rules to drift.
 *
 * PRE-FILLED, unlike every other picker in this app. The DEF-7 rule against
 * pre-filling is about pickers that read as already answered — a party, a
 * reason code, a tender. This is the opposite act: the whole point is to show
 * what is currently on the bill so the one wrong character can be found and
 * changed, and an empty form would make a correction into a re-entry.
 *
 * NO FEE AND NO PLATFORM here. The fee is priced into the bill's stored totals
 * and the commission rate is snapshotted at open, so correcting a street name
 * must not be a door to either — since T6 (2026-10-06) they have their OWN
 * door, `ChargesDialog`, on the cashier's rung and OPEN only. The hint says so,
 * because a form with three boxes on a delivery bill invites the question.
 */
export function CustomerDialog({
  invoice,
  isSubmitting,
  onCancel,
  onConfirm,
}: {
  invoice: Invoice;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (input: {
    customerName: string;
    customerPhone: string;
    deliveryAddress: string;
  }) => void | Promise<void>;
}) {
  const t = useTranslate();
  const isDelivery = invoice.channel === "DELIVERY";
  const [customerName, setName] = useState(invoice.customerName ?? "");
  const [customerPhone, setPhone] = useState(invoice.customerPhone ?? "");
  const [deliveryAddress, setAddress] = useState(invoice.deliveryAddress ?? "");

  // A correction is dirty the moment any of the three boxes stops matching the
  // bill — including a CLEARED one, which is the edit this sheet exists for.
  // T11 — live, and naming the gap: clearing the phone alone now warns too.
  const undeliverable = undeliverableReason({
    channel: invoice.channel,
    customerPhone,
    deliveryAddress,
  });

  const guard = useDiscardGuard(
    customerName !== (invoice.customerName ?? "") ||
      customerPhone !== (invoice.customerPhone ?? "") ||
      (isDelivery && deliveryAddress !== (invoice.deliveryAddress ?? "")),
    onCancel,
  );

  return (
    <>
    <Modal
      isOpen
      onClose={guard.requestClose}
      size="sm"
      title={t((m) => m.pos.customerEditTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="customer-dialog"
      footerActions={
        <>
          <Button
            variant="outline"
            size="touch"
            disabled={isSubmitting}
            onClick={guard.requestClose}
            data-testid="customer-cancel"
          >
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            variant="primary"
            size="touch"
            className="font-extrabold"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            data-testid="customer-save"
            onClick={() =>
              void onConfirm({
                customerName,
                customerPhone,
                // Never sent on a takeaway: the field is not on screen, and a
                // stale value from a channel this bill is not would be a fact
                // nobody stated. The server normalises it away too.
                deliveryAddress: isDelivery ? deliveryAddress : "",
              })
            }
          >
            {t((m) => m.common.save)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <FormField
          name="customerName"
          label={t((m) => m.pos.offPremiseNameLabel)}
        >
          <TextInput
            name="customerName"
            value={customerName}
            maxLength={120}
            dir="auto"
            onChange={(event) => setName(event.target.value)}
            data-testid="customer-name"
          />
        </FormField>

        <FormField
          name="customerPhone"
          label={t((m) => m.pos.offPremisePhoneLabel)}
        >
          <TextInput
            name="customerPhone"
            value={customerPhone}
            maxLength={32}
            dir="ltr"
            inputMode="tel"
            onChange={(event) => setPhone(event.target.value)}
            data-testid="customer-phone"
          />
        </FormField>

        {isDelivery ? (
          <FormField
            name="deliveryAddress"
            label={t((m) => m.pos.offPremiseAddressLabel)}
          >
            <TextInput
              name="deliveryAddress"
              value={deliveryAddress}
              maxLength={500}
              dir="auto"
              onChange={(event) => setAddress(event.target.value)}
              data-testid="customer-address"
            />
          </FormField>
        ) : null}

        {/* WHAT THIS SCREEN DOES NOT TOUCH. On a delivery bill three boxes
            invite "and the fee?", and the honest answer has to be on the
            screen rather than in a refusal afterwards. */}
        <p className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
          {t((m) => m.pos.customerEditHint)}
        </p>

        {/* The same warning the open dialog carries, live: clearing both the
            phone and the address leaves a delivery nobody can deliver, and a
            correction is exactly where that can happen by accident. */}
        {undeliverable ? (
          <p
            role="alert"
            className="rounded-[var(--radius-list)] border border-[color:var(--warning)] px-3 py-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold text-[color:var(--warning)]"
            data-testid="customer-undeliverable"
            data-reason={undeliverable}
          >
            {undeliverableMessage(undeliverable, t)}
          </p>
        ) : null}

      </div>
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="customer-discard-prompt"
    />
    </>
  );
}
